/**
 * render — builds picker fieldset markup from the ProductSchema options.
 *
 * Client-side replacement for the old pf-picker-option / pf-picker-option-item
 * Liquid snippets. The emitted DOM (class names, [data-layout], data-attributes)
 * intentionally matches those snippets so pf-picker-option.css applies unchanged
 * and the selection engine's data-pf-option-name / data-pf-raw-value bindings work.
 *
 * Interaction is decided per value:
 *   - value.targetHandle set  → cross-product <a> link (navigation)
 *   - value.targetHandle null → in-product <input type=radio> (local selection)
 * except for link-oriented layouts (x-split, image-meta) which always render
 * links, with the current value shown as an active, non-navigating link.
 */

import { deriveLayout } from './layout.js';
import { findVariant } from './selection-resolver.js';

const LINK_LAYOUTS = new Set(['x-split', 'image-meta']);

/**
 * Decide whether an option value should render greyed/unavailable.
 *
 * @param {object} schema
 * @param {string} optionName
 * @param {object} value
 * @param {Record<string,string>} selection
 * @returns {boolean}
 */
export function isValueUnavailable(schema, optionName, value, selection) {
  // If the selected variant doesn't have this option at all (e.g. Mooring
  // Iron on a without-iron buoy), treat it as N/A and grey it out.
  const current = findVariant(schema.variants, selection);
  if (current && !(optionName in (current.optionValues ?? {}))) return true;

  return value.selectable === false;
}

/** Escape a string for use inside an HTML attribute value. */
function escAttr(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Escape a string for use as HTML text content. */
function escHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Presentation-only label massaging — a client concern, not a data one.
 *
 * The API returns the same consistent, fully-qualified labels to every client
 * (e.g. "Fender Fits-A-2"). The data is correct; this storefront just prefers a
 * terser presentation in the compact Size grid, where the "Size" legend already
 * supplies the context — so we drop the redundant boilerplate prefix that repeats
 * on every value, leaving the bare series name ("A-2"). This is deliberately NOT
 * pushed to the API (that would bend a shared contract to one client's taste).
 *
 * Display-only: applied to `displayLabel` (visible text) — never to `rawValue`,
 * so variant matching, URL params, and cross-product navigation are unaffected.
 * The prefix is anchored, so it only ever matches the fender-cover Size labels;
 * buoy sizes ("A-2") and every other option are left untouched.
 */
const LABEL_PREFIX_STRIP = /^Fender Fits-/;

function presentLabel(label) {
  return String(label ?? '').replace(LABEL_PREFIX_STRIP, '');
}

/** Drop a trailing ".0" from a number-like value: "10.0" → "10". */
function trimZero(n) {
  const s = String(n);
  return s.endsWith('.0') ? s.slice(0, -2) : s;
}

/**
 * Format a Dimensions object into US-standard + metric label strings.
 * Uses width × length (the historical "diameter × length" presentation).
 * @param {{width?:number,height?:number,length?:number,unit?:string}|null} dim
 * @returns {{ us: string, metric: string }|null}
 */
export function formatDimensions(dim) {
  if (!dim) return null;
  const { width, length } = dim;
  if (width == null || length == null) return null;

  const us = `${trimZero(width)}" x ${trimZero(length)}"`;
  const toCm = (v) => trimZero(Math.round(v * 2.54 * 10) / 10);
  const metric = `${toCm(width)} x ${toCm(length)} cm`;
  return { us, metric };
}

function tooltipText(value) {
  return value.metaLabel || value.displayLabel;
}

function subject(optionName, label) {
  return /^(Size|Color)$/.test(optionName)
    ? label
    : `${label} ${optionName.replace(/ Color$/, '').toLowerCase()}`;
}

function blocker(schema, selection, value) {
  const name = value.blockedBy;
  if (!name || selection[name] == null) return null;
  const current = schema.options
    .find((o) => o.name === name)
    ?.values.find((v) => v.rawValue === selection[name]);
  return {
    name,
    label: subject(
      name,
      presentLabel(current?.displayLabel ?? selection[name])
    ),
  };
}

function blockedOverlay(blocked) {
  const where = blocked ? `in ${blocked.label}` : 'with this selection';
  return `<span class="pf-picker__why" aria-hidden="true"><b>Not available</b><small>${escHtml(where)}</small></span>`;
}

function blockedMessage(optionName, value, blocked) {
  const what = `<b>${escHtml(subject(optionName, presentLabel(value.displayLabel)))}</b>`;
  return blocked
    ? `${what} isn't available in <b>${escHtml(blocked.label)}</b>. Choose a different ${escHtml(blocked.name.toLowerCase())} first.`
    : `${what} isn't available with this selection.`;
}

export const SWATCH_PLACEHOLDER_SVG =
  '<svg class="block w-full h-full pf-picker__swatch-placeholder" viewBox="0 0 100 100" role="img" aria-label="Image not available" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">' +
  '<rect width="100" height="100" fill="currentColor" opacity="0.05"/>' +
  '<rect x="20.5" y="26.5" width="59" height="47" rx="5" fill="none" stroke="currentColor" stroke-width="3" opacity="0.35"/>' +
  '<circle cx="38" cy="43" r="5.5" fill="currentColor" opacity="0.35"/>' +
  '<path d="M27 68 L45 49 L57 61 L66 53 L79 66 L79 71 Q79 73.5 76.5 73.5 L23.5 73.5 Q21 73.5 21 71 Z" fill="currentColor" opacity="0.35"/>' +
  '</svg>';

/**
 * Swatch <img>, or the generic placeholder when there's no image (or `blank`).
 * @param {object} value
 * @param {boolean} [blank]  force the placeholder even if a URL exists
 */
function swatchImg(value, blank = false) {
  return !blank && value.swatchImageUrl
    ? `<img src="${escAttr(value.swatchImageUrl)}" alt="${escAttr(value.displayLabel)}" class="block w-full h-full object-cover">`
    : SWATCH_PLACEHOLDER_SVG;
}

/**
 * Rewrite a ropehold swatch URL to the **White** color of the same ropehold top.
 * A Ropehold swatch depicts the *current color* with that top; for a color not
 * sold in a ropehold the API returns an unrelated fallback color (e.g. orange).
 * The White image exists for every ropehold, so swap the color token for
 * `white`: ".../Polyform-A2-SF-blacktop-orange.jpg?v=1" → "...-blacktop-white.jpg?v=1".
 * Returns null only when the URL doesn't carry a `{top}-{color}` pattern (so the
 * caller can fall back to a blank placeholder). When the current color already
 * IS White, or the API already picked the White member, the URL is a valid
 * `...top-white*.jpg` and is returned unchanged: rewriting it would strip a
 * Shopify uniqueness suffix (`greytop-white_01a0f72c-….jpg`) and 404.
 * @param {string|null|undefined} url
 * @returns {string|null}
 */
function toWhiteRopeholdImage(url) {
  if (!url) return null;
  if (/top[-_]white/i.test(url)) return url;
  const re = /(top[-_])[^/?#.]+(\.[a-z0-9]+)/i;
  if (!re.test(url)) return null; // no {top}-{color} pattern — can't derive
  return url.replace(re, '$1white$2');
}

/**
 * Build one <li> item.
 * @param {object} value
 * @param {object} ctx  { layout, optionName, isSelected, useLink, currentUrl, optionId, index }
 */
function buildItem(value, ctx) {
  const {
    layout,
    optionName,
    isSelected,
    useLink,
    currentUrl,
    optionId,
    index,
    unavailable,
    blocked,
  } = ctx;
  const isBlocked = value.selectable === false;
  const liClasses = [
    'pf-picker__item',
    'relative',
    useLink && layout === 'x-split' ? 'flex' : '',
    unavailable ? 'pf-picker__item--unavailable' : '',
    isBlocked ? 'pf-picker__item--blocked' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const blockedAttr = isBlocked
    ? ` data-pf-blocked="${escAttr(blockedMessage(optionName, value, blocked))}"`
    : '';
  const overlay = isBlocked ? blockedOverlay(blocked) : '';

  const dataAttrs = `data-pf-option-name="${escAttr(optionName)}" data-pf-raw-value="${escAttr(value.rawValue)}"`;

  if (useLink) {
    // Cross-product navigation link (or the current value as a non-navigating,
    // active link back to this product page).
    const href =
      isSelected || !value.targetHandle
        ? currentUrl || '#'
        : `/products/${encodeURIComponent(value.targetHandle)}`;
    // The active value never navigates (even on legacy products where its own
    // value carries a targetHandle) — clicking the current selection is a no-op.
    const targetAttr =
      !isSelected && value.targetHandle
        ? ` data-target-handle="${escAttr(value.targetHandle)}"`
        : '';
    const title = unavailable
      ? `${tooltipText(value)} (not available for this selection)`
      : tooltipText(value);

    let inner;
    if (layout === 'image-meta') {
      let ropeImg;
      if (unavailable) {
        const whiteUrl = toWhiteRopeholdImage(value.swatchImageUrl);
        ropeImg = whiteUrl
          ? swatchImg({ ...value, swatchImageUrl: whiteUrl })
          : swatchImg(value, true);
      } else {
        ropeImg = swatchImg(value);
      }
      inner =
        `<div class="relative">${ropeImg}${overlay}</div>` +
        `<div class="text-center pf-picker__details">` +
        `<div class="text-xs pf-picker__title">${escHtml(value.displayLabel)}</div>` +
        `</div>`;
    } else if (layout === 'x-split') {
      const dims = formatDimensions(value.dimensions);
      let meta = '';
      if (value.metaLabel) {
        meta = `<span class="pf-picker__meta">${escHtml(value.metaLabel)}</span>`;
      } else if (dims) {
        meta = `<span class="pf-picker__meta" data-size-dimensions data-dimensions-us-standard="${escAttr(dims.us)}" data-dimensions-metric="${escAttr(dims.metric)}">${escHtml(dims.us)}</span>`;
      }
      inner = `<span class="pf-picker__title">${escHtml(value.displayLabel)}</span>${meta}${overlay}`;
    } else if (layout === 'swatch') {
      // swatch-style link (image only)
      inner = swatchImg(value) + overlay;
    } else if (layout === 'y-split') {
      // Cross-product y-split link — mirror the radio's title markup so the
      // label text shows (these values carry no swatch image).
      inner = `<span class="pf-picker__title">${escHtml(value.displayLabel)}</span>${overlay}`;
    } else {
      // text (default) cross-product link — mirror the text radio's title
      // markup; without this the value renders as an empty (textless) box.
      inner = `<span class="block text-xs pf-picker__title">${escHtml(value.displayLabel)}</span>${overlay}`;
    }

    return `<li class="${liClasses}"${blockedAttr}><a href="${escAttr(href)}" class="pf-picker__label w-full h-full relative cursor-pointer transition${isSelected ? ' is-active' : ''}" title="${escAttr(title)}"${targetAttr}${isBlocked ? ' aria-disabled="true"' : ''} ${dataAttrs}>${inner}</a></li>`;
  }

  // Local radio item.
  const inputId = `${optionId}-opt-${index}`;
  const input =
    `<input type="radio" class="opt-btn visually-hidden focus-label js-option${unavailable ? ' is-unavailable' : ''}" ` +
    `name="${escAttr(optionId)}-option" id="${escAttr(inputId)}" value="${escAttr(value.rawValue)}" ` +
    `${dataAttrs} required${isSelected ? ' checked' : ''}${isBlocked ? ' disabled' : ''}>`;

  const radioTitleAttr =
    layout === 'swatch' || value.metaLabel
      ? ` title="${escAttr(tooltipText(value))}"`
      : '';

  let label;
  if (layout === 'swatch') {
    label = `<label for="${escAttr(inputId)}" class="pf-picker__label cursor-pointer transition"${radioTitleAttr}>${swatchImg(value)}${overlay}</label>`;
  } else if (layout === 'y-split') {
    label = `<label for="${escAttr(inputId)}" class="pf-picker__label cursor-pointer text-center transition"${radioTitleAttr}><span class="pf-picker__title">${escHtml(value.displayLabel)}</span>${overlay}</label>`;
  } else {
    // text (and any radio fallback)
    label = `<label for="${escAttr(inputId)}" class="pf-picker__label cursor-pointer text-center transition"${radioTitleAttr}><span class="block text-xs pf-picker__title">${escHtml(value.displayLabel)}</span>${overlay}</label>`;
  }

  return `<li class="${liClasses}"${blockedAttr}>${input}${label}</li>`;
}

/**
 * Footer markup for a measurement-bearing option (x-split / A-series sizes):
 * a left-aligned anchor to the on-page size chart. Emitted only when the host
 * page actually contains the anchor target (passed via ctx.sizeChart).
 * @param {{ anchor: string, label: string }|null} sizeChart
 * @returns {string}
 */
function buildSizeChartFooter(sizeChart) {
  if (!sizeChart || !sizeChart.anchor) return '';
  return `<div class="pf-picker__footer text-left">
    <a href="${escAttr(sizeChart.anchor)}" class="pf-picker__size-chart-link">${escHtml(sizeChart.label)}</a>
  </div>`;
}

/**
 * Build one <fieldset> for an option.
 * @param {object} option
 * @param {Record<string,string>} selection
 * @param {object} ctx  { uid, currentUrl, optIndex, schema, sizeChart }
 */
function buildFieldset(option, selection, ctx) {
  const layout = deriveLayout(option);
  const forceLink = LINK_LAYOUTS.has(layout);
  const optionId = `pf-picker-${ctx.uid}-${ctx.optIndex}`;
  const selectedRaw = (selection ?? {})[option.name];

  const gridClass =
    layout === 'x-split'
      ? 'pf-picker__grid grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3'
      : 'pf-picker__options grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3';

  // Massage display labels for presentation once, up front — every downstream
  // consumer (item markup, legend selected-value label) then reads the terser
  // label while `rawValue` stays intact for matching. See presentLabel.
  const values = option.values.map((v) => ({
    ...v,
    displayLabel: presentLabel(v.displayLabel),
  }));

  const items = values
    .map((value, index) => {
      // A value in another sub-product (targetHandle set) navigates; the anchor's
      // own values (targetHandle null) select locally. Per-partition emission makes
      // targetHandle the reliable signal, so two same-color swatches (with vs.
      // without iron) are told apart by it — and only the local one is "selected".
      const isSelected = value.rawValue === selectedRaw && !value.targetHandle;
      const useLink = forceLink || Boolean(value.targetHandle);
      return buildItem(value, {
        layout,
        optionName: option.name,
        isSelected,
        useLink,
        currentUrl: ctx.currentUrl,
        optionId,
        index,
        unavailable: isValueUnavailable(
          ctx.schema,
          option.name,
          value,
          selection
        ),
        blocked: blocker(ctx.schema, selection, value),
      });
    })
    .join('\n');

  const selectedValue =
    selectedRaw != null ? values.find((v) => v.rawValue === selectedRaw) : null;
  const selectedLabel = selectedValue
    ? tooltipText(selectedValue)
    : (selectedRaw ?? '');
  const legendExtra = selectedLabel
    ? `<span class="font-normal" data-selected-value-label>- ${escHtml(selectedLabel)}</span>`
    : '';

  // Size chart link only for measurement-bearing options (the x-split layout,
  // derived from values carrying `dimensions` — i.e. A-series sizes).
  const footer =
    layout === 'x-split' ? buildSizeChartFooter(ctx.sizeChart) : '';

  return `<fieldset class="pf-picker pf-picker--layout-${layout}" data-layout="${layout}" data-pf-option-name="${escAttr(option.name)}">
  <legend class="label">${escHtml(option.name)}${legendExtra}</legend>
  <ul class="${gridClass}">
    ${items}
  </ul>
  ${footer}
</fieldset>`;
}

/**
 * Render all option fieldsets for a schema.
 * Options with fewer than two values, no cross-product jumps and no swatch
 * image are skipped (nothing to choose or show).
 *
 * @param {object} schema
 * @param {Record<string,string>} selection
 * @param {{ uid: string|number, currentUrl?: string, sizeChart?: {anchor: string, label: string}|null }} opts
 * @returns {string} concatenated fieldset HTML (empty string when nothing to render)
 */
export function renderPicker(schema, selection = {}, opts = {}) {
  const uid = opts.uid ?? '0';
  const currentUrl = opts.currentUrl ?? '';
  const sizeChart = opts.sizeChart ?? null;
  const sel = selection ?? {};

  return schema.options
    .filter((opt) => {
      const values = opt.values || [];
      if (values.length > 1) return true;
      return values.some((v) => v.targetHandle || v.swatchImageUrl);
    })
    .map((opt, optIndex) =>
      buildFieldset(opt, sel, { uid, currentUrl, optIndex, schema, sizeChart })
    )
    .join('\n');
}
